const RESOURCE_TYPE_INTERNAL = 1
const RESOURCE_TYPE_EXTERNAL = 2
const RESOURCE_KIND_PATH = 1
const RESOURCE_KIND_MENU = 2
const RESOURCE_KIND_BUTTON = 3
const OPEN_MODE_EMBEDDED = 1
const OPEN_MODE_NEW_TAB = 2

function normalizeInternalPath(value) {
  const source = String(value || '').trim()
  if (source.includes('\\')) return ''
  const sourcePath = source.split(/[?#]/, 1)[0]
  if (!sourcePath || sourcePath.includes('//') || /^[a-z][a-z\d+.-]*:/i.test(sourcePath)) return ''
  const path = sourcePath.startsWith('/') ? sourcePath : `/${sourcePath}`
  return path.length > 1 ? path.replace(/\/+$/, '') : path
}

function normalizeViewKey(value) {
  let key = String(value || '').trim().replace(/\\/g, '/')
  key = key.replace(/^\.\//, '').replace(/\.vue$/i, '')
  if (key.toLowerCase().endsWith('/index')) key = key.slice(0, -6)
  return `/${key}`.replace(/\/{2,}/g, '/').replace(/\/$/, '').toLowerCase()
}

function safeExternalUrl(value) {
  try {
    const url = new URL(String(value || '').trim())
    return ['http:', 'https:'].includes(url.protocol) ? url.href : ''
  } catch (error) {
    return ''
  }
}

function compareResources(left, right) {
  const orderCompared = Number(left.orderNo || 0) - Number(right.orderNo || 0)
  if (orderCompared) return orderCompared
  return String(left.resourceId || '').localeCompare(String(right.resourceId || ''))
}

export function createViewResolver(entries = []) {
  const viewByPath = new Map()
  entries.forEach((entry) => {
    const key = normalizeViewKey(entry?.key)
    if (key && typeof entry?.loader === 'function') viewByPath.set(key, entry.loader)
  })

  return (routePath) => viewByPath.get(normalizeViewKey(normalizeInternalPath(routePath))) || null
}

export function buildAccessModel(resources = [], options = {}) {
  const rows = Array.isArray(resources) ? [...resources].sort(compareResources) : []
  const resolveView = options.resolveView || (() => null)
  const externalFrameComponent = options.externalFrameComponent || null
  const missingViewComponent = options.missingViewComponent || null
  const reservedPaths = new Set(options.reservedPaths || ['/', '/login', '/intro'])
  const buttonPermissionList = []
  const permissionSet = new Set()
  const routeRecords = []
  const errors = []
  const menuById = new Map()

  rows.forEach((row) => {
    const resourceId = String(row?.resourceId || '').trim()
    const resourceKind = Number(row?.resourceKind)
    if (!resourceId) return

    if (resourceKind === RESOURCE_KIND_BUTTON) {
      const permissionCode = String(row.permissionCode || '').trim()
      if (permissionCode && !permissionSet.has(permissionCode)) {
        permissionSet.add(permissionCode)
        buttonPermissionList.push(permissionCode)
      }
      return
    }

    if (![RESOURCE_KIND_PATH, RESOURCE_KIND_MENU].includes(resourceKind)) return

    const resourceType = Number(row.resourceType || RESOURCE_TYPE_INTERNAL)
    const openMode = Number(row.openMode || OPEN_MODE_EMBEDDED)
    let url = null
    let resolvedResourceUrl = row.resourceUrl || null

    if (resourceKind === RESOURCE_KIND_MENU) {
      if (resourceType === RESOURCE_TYPE_EXTERNAL) {
        const externalUrl = safeExternalUrl(row.resourceUrl)
        if (!externalUrl) {
          errors.push({ resourceId, code: 'INVALID_EXTERNAL_URL' })
          return
        }
        resolvedResourceUrl = externalUrl
        if (openMode === OPEN_MODE_EMBEDDED) {
          if (!externalFrameComponent) {
            errors.push({ resourceId, code: 'EXTERNAL_FRAME_UNAVAILABLE' })
            return
          }
          url = `/external/${encodeURIComponent(resourceId)}`
          routeRecords.push({
            path: url,
            name: `resource-${resourceId}`,
            component: externalFrameComponent,
            meta: {
              resourceId,
              title: row.resourceName,
              resourceType,
              openMode,
              externalUrl,
            },
          })
        } else {
          url = externalUrl
        }
      } else {
        const internalPath = normalizeInternalPath(row.resourceUrl)
        if (!internalPath) {
          errors.push({ resourceId, code: 'INVALID_INTERNAL_PATH' })
          return
        }
        resolvedResourceUrl = internalPath
        url = internalPath
        if (!reservedPaths.has(internalPath)) {
          const resolvedComponent = resolveView(internalPath)
          if (!resolvedComponent) {
            errors.push({ resourceId, code: 'VIEW_COMPONENT_NOT_FOUND' })
            if (!missingViewComponent) return
          }
          routeRecords.push({
            path: internalPath,
            name: `resource-${resourceId}`,
            component: resolvedComponent || missingViewComponent,
            meta: {
              resourceId,
              title: row.resourceName,
              resourceType,
              openMode,
              resourceUnavailable: !resolvedComponent,
              requestedResourceUrl: internalPath,
            },
          })
        }
      }
    }

    menuById.set(resourceId, {
      menuId: resourceId,
      menuName: row.resourceName,
      parentId: row.parentId ? String(row.parentId) : null,
      level: Number(row.level || 1),
      url,
      icon: row.menuIcon || null,
      resourceType,
      resourceKind,
      openMode,
      resourceUrl: resolvedResourceUrl,
      orderNo: Number(row.orderNo || 0),
      children: [],
    })
  })

  const roots = []
  menuById.forEach((menu) => {
    const parent = menu.parentId ? menuById.get(menu.parentId) : null
    if (parent) parent.children.push(menu)
    else roots.push(menu)
  })

  const pruneAndSort = (nodes) => nodes
    .sort(compareResources)
    .map((node) => ({ ...node, children: pruneAndSort(node.children || []) }))
    .filter((node) => node.resourceKind === RESOURCE_KIND_MENU || node.children.length > 0)
    .map((node) => (node.children.length ? node : { ...node, children: undefined }))

  return {
    menuList: pruneAndSort(roots),
    buttonPermissionList,
    routeRecords,
    errors,
  }
}

export function resolveMenuNavigation(menu, newWindow = false) {
  if (!menu?.url) return null
  if (Number(menu.openMode) !== OPEN_MODE_NEW_TAB && !newWindow) {
    return { mode: 'route', target: menu.url }
  }
  if (Number(menu.resourceType) === RESOURCE_TYPE_EXTERNAL && Number(menu.openMode) === OPEN_MODE_NEW_TAB) {
    return { mode: 'new-tab', target: menu.resourceUrl }
  }
  return { mode: 'internal-new-tab', target: menu.url }
}
